import { useEffect, useMemo, useState } from "react";
import {
  Banner,
  Button,
  Card,
  CardGrid,
  Cell,
  Checkbox,
  FormItem,
  Group,
  Header,
  Input,
  Select,
  Snackbar,
  Text,
  Textarea,
  Title
} from "@vkontakte/vkui";
import {
  Icon24ArticleOutline,
  Icon24CheckCircleOutline,
  Icon24PenOutline,
  Icon24MoneyCircleOutline,
  Icon24Search,
  Icon24StatisticsOutline
} from "@vkontakte/icons";
import type { CheckCardResponse, DiscountRangeRule, DiscountRule, Offer, Partner } from "../api/types";
import { api, errorMessage } from "../api/client";
import { formatDate, formatMoney } from "../shared/format";
import { Page } from "../components/Page";
import { AccessDenied, EmptyState, ErrorState, LoadingState } from "../components/States";
import { PartnerMedia } from "../components/PartnerMedia";
import { useAuthStore } from "../app/authStore";

type Dashboard = {
  partner: Partner;
  metrics: {
    viewsCount: number;
    checksCount: number;
    clientsCount: number;
    redemptionsCount: number;
    discountAmount: number;
  };
  latestChecks: Array<{ id: string; createdAt: string; metadata?: { cardNumber?: string; result?: string } }>;
  latestRedemptions: Array<{
    id: string;
    cardNumber: string;
    memberFullNameSnapshot: string;
    discountAmount: number;
    finalAmount: number;
    createdAt: string;
  }>;
};

const membershipLevels: DiscountRule["membershipLevel"][] = ["Basic", "Silver", "Gold", "VIP"];
type DiscountMode = "percent" | "fixed";

function toDateTime(value?: string | null) {
  return value ? new Date(value).toISOString().slice(0, 16) : "";
}

function fromDateTime(value?: string | null) {
  return value ? new Date(value).toISOString() : null;
}

export function PartnerDashboardPage({ navigate }: { navigate: (path: string) => void }) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);

  useEffect(() => {
    setLoading(true);
    api
      .get<Dashboard>("/partner/dashboard")
      .then((response) => {
        setDashboard(response.data);
        setError("");
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedPartnerId]);

  if (loading) {
    return (
      <Page title="Кабинет партнёра">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Кабинет партнёра">
        {error.toLowerCase().includes("access") ? <AccessDenied /> : <ErrorState message={error} />}
      </Page>
    );
  }

  if (!dashboard) {
    return null;
  }

  return (
    <Page title="Кабинет партнёра">
      <PartnerScopeSelector />
      <Group header={<Header>{dashboard.partner.title}</Header>}>
        <div className="metric-grid">
          <Metric title="Просмотры" value={String(dashboard.metrics.viewsCount)} />
          <Metric title="Проверки" value={String(dashboard.metrics.checksCount)} />
          <Metric title="Клиенты" value={String(dashboard.metrics.clientsCount)} />
          <Metric title="Выдано скидок" value={formatMoney(dashboard.metrics.discountAmount)} />
        </div>
      </Group>

      <Group header={<Header>Действия</Header>}>
        <div className="toolbar">
          <Button before={<Icon24Search />} onClick={() => navigate("/partner/check")}>
            Проверить карту
          </Button>
          <Button before={<Icon24PenOutline />} mode="secondary" onClick={() => navigate("/partner/store")}>
            Моя страница
          </Button>
          <Button before={<Icon24MoneyCircleOutline />} mode="secondary" onClick={() => navigate("/partner/discounts")}>
            Скидки
          </Button>
          <Button before={<Icon24ArticleOutline />} mode="secondary" onClick={() => navigate("/partner/offers")}>
            Акции
          </Button>
          <Button before={<Icon24StatisticsOutline />} mode="tertiary" disabled>
            Продвижение
          </Button>
        </div>
      </Group>

      <Group header={<Header>Последние проверки</Header>}>
        {dashboard.latestChecks.length ? (
          dashboard.latestChecks.map((event) => (
            <Cell key={event.id} subtitle={formatDate(event.createdAt)} after={event.metadata?.result}>
              Карта {event.metadata?.cardNumber || "без номера"}
            </Cell>
          ))
        ) : (
          <EmptyState text="Проверок ещё нет." />
        )}
      </Group>

      <Group header={<Header>Последние скидки</Header>}>
        {dashboard.latestRedemptions.length ? (
          dashboard.latestRedemptions.map((entry) => (
            <Cell key={entry.id} subtitle={`${entry.memberFullNameSnapshot} · ${formatDate(entry.createdAt)}`} after={formatMoney(entry.discountAmount)}>
              Карта {entry.cardNumber}
            </Cell>
          ))
        ) : (
          <EmptyState text="Фиксаций скидок ещё нет." />
        )}
      </Group>
    </Page>
  );
}

function Metric({ title, value }: { title: string; value: string }) {
  return (
    <div className="metric">
      <Text className="muted">{title}</Text>
      <div className="metric-value">{value}</div>
    </div>
  );
}

function PartnerScopeSelector() {
  const partners = useAuthStore((state) => state.partners);
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);
  const setSelectedPartnerId = useAuthStore((state) => state.setSelectedPartnerId);

  if (partners.length <= 1) {
    return null;
  }

  return (
    <Group header={<Header>Активная организация</Header>}>
      <FormItem top="Кабинет партнёра">
        <Select
          value={selectedPartnerId || partners[0].id}
          onChange={(event) => setSelectedPartnerId(event.target.value)}
          options={partners.map((partner) => ({
            label: `${partner.title} · ${partner.accessRole === "admin" ? "администратор" : partner.accessRole === "owner" ? "владелец" : "сотрудник"}`,
            value: partner.id
          }))}
        />
      </FormItem>
    </Group>
  );
}

export function PartnerCheckPage() {
  const [cardNumber, setCardNumber] = useState("000001");
  const [result, setResult] = useState<CheckCardResponse | null>(null);
  const [amount, setAmount] = useState(5000);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountMode, setDiscountMode] = useState<DiscountMode>("percent");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);

  const fixedRanges = result?.isValid ? result.partnerDiscount.fixedDiscountRanges : [];
  const applicableFixedRange = useMemo(() => {
    return fixedRanges
      .filter((rule) => amount >= rule.minPurchaseAmount && (rule.maxPurchaseAmount === null || amount <= rule.maxPurchaseAmount))
      .sort((left, right) => right.discountAmount - left.discountAmount)[0];
  }, [amount, fixedRanges]);
  const discountAmount = useMemo(() => {
    if (discountMode === "fixed") {
      return applicableFixedRange?.discountAmount ?? 0;
    }

    return Math.round((amount * discountPercent) + Number.EPSILON) / 100;
  }, [amount, applicableFixedRange, discountMode, discountPercent]);
  const finalAmount = useMemo(() => Math.round((amount - discountAmount + Number.EPSILON) * 100) / 100, [amount, discountAmount]);

  useEffect(() => {
    setResult(null);
    setSuccess("");
    setError("");
  }, [selectedPartnerId]);

  const checkCard = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post<CheckCardResponse>("/partner/check-card", { cardNumber });
      setResult(response.data);
      if (response.data.isValid) {
        setDiscountPercent(response.data.partnerDiscount.discountPercent);
        setDiscountMode(response.data.partnerDiscount.discountPercent > 0 ? "percent" : "fixed");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const createRedemption = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/partner/redemptions", {
        cardNumber,
        amountBeforeDiscount: amount,
        discountPercent: discountMode === "percent" ? discountPercent : 0,
        discountAmount,
        finalAmount,
        comment
      });
      setSuccess("Скидка зафиксирована");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page title="Проверка карты">
      <PartnerScopeSelector />
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Номер карты</Header>}>
        <FormItem top="Членская карта">
          <Input value={cardNumber} onChange={(event) => setCardNumber(event.target.value)} placeholder="000001" />
        </FormItem>
        <Button loading={loading} before={<Icon24Search />} onClick={checkCard}>
          Проверить
        </Button>
      </Group>

      {result ? (
        <Group header={<Header>Результат</Header>}>
          {result.isValid ? (
            <>
              <Banner header="Карта активна" subheader={`${result.member.fullName} · ${result.member.level} · до ${formatDate(result.member.expiresAt)}`} />
              <Cell subtitle={result.partnerDiscount.conditionsText} after={`${result.partnerDiscount.discountPercent}%`}>
                Процентная скидка партнёра
              </Cell>
              {result.partnerDiscount.minPurchaseAmount ? (
                <Cell subtitle="Минимальная сумма покупки">{formatMoney(result.partnerDiscount.minPurchaseAmount)}</Cell>
              ) : null}
              {result.partnerDiscount.fixedDiscountRanges.length ? (
                result.partnerDiscount.fixedDiscountRanges.map((rule) => (
                  <Cell
                    key={rule.id}
                    subtitle={`${formatMoney(rule.minPurchaseAmount)}${rule.maxPurchaseAmount ? ` - ${formatMoney(rule.maxPurchaseAmount)}` : " и выше"} · ${rule.conditionsText}`}
                    after={formatMoney(rule.discountAmount)}
                  >
                    {rule.title}
                  </Cell>
                ))
              ) : null}
            </>
          ) : (
            <Banner
              header="Скидку применить нельзя"
              subheader={result.member ? `${result.reason}: ${result.member.fullName}, карта до ${formatDate(result.member.expiresAt)}` : result.reason}
            />
          )}
        </Group>
      ) : null}

      {result?.isValid ? (
        <Group header={<Header>Фиксация скидки</Header>}>
          <div className="form-row">
            <FormItem top="Сумма до скидки">
              <Input type="number" value={String(amount)} onChange={(event) => setAmount(Number(event.target.value))} />
            </FormItem>
            <FormItem top="Тип скидки">
              <Select
                value={discountMode}
                onChange={(event) => setDiscountMode(event.target.value as DiscountMode)}
                options={[
                  { label: "Процентная", value: "percent" },
                  { label: "Фиксированная по сумме", value: "fixed" }
                ]}
              />
            </FormItem>
          </div>
          {discountMode === "percent" ? (
            <FormItem top="Процент скидки">
              <Input type="number" value={String(discountPercent)} onChange={(event) => setDiscountPercent(Number(event.target.value))} />
            </FormItem>
          ) : (
            <Banner
              header={applicableFixedRange ? applicableFixedRange.title : "Нет подходящего диапазона"}
              subheader={
                applicableFixedRange
                  ? `Фиксированная скидка ${formatMoney(applicableFixedRange.discountAmount)} при этой сумме покупки.`
                  : "Измените сумму покупки или настройте диапазон скидки."
              }
            />
          )}
          <Cell subtitle="Сумма скидки">{formatMoney(discountAmount)}</Cell>
          <Cell subtitle="Итоговая сумма">{formatMoney(finalAmount)}</Cell>
          <FormItem top="Комментарий">
            <Textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Покупка экипировки" />
          </FormItem>
          <Button disabled={discountAmount <= 0} loading={loading} before={<Icon24CheckCircleOutline />} onClick={createRedemption}>
            Зафиксировать скидку
          </Button>
        </Group>
      ) : null}

      {success ? <Snackbar onClose={() => setSuccess("")}>{success}</Snackbar> : null}
    </Page>
  );
}

export function PartnerStorePage() {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ partner: Partner }>("/partner/store")
      .then((response) => {
        setPartner(response.data.partner);
        setError("");
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedPartnerId]);

  const update = (key: keyof Partner, value: string) => {
    setPartner((current) => (current ? { ...current, [key]: value } : current));
  };

  const save = async () => {
    if (!partner) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      const response = await api.patch<{ partner: Partner }>("/partner/store", partner);
      setPartner(response.data.partner);
      setSuccess("Страница партнёра сохранена");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page title="Моя страница">
        <LoadingState />
      </Page>
    );
  }

  if (error && !partner) {
    return (
      <Page title="Моя страница">
        <AccessDenied />
      </Page>
    );
  }

  if (!partner) {
    return null;
  }

  return (
    <Page title="Моя страница">
      <PartnerScopeSelector />
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Предпросмотр</Header>}>
        <PartnerMedia
          title={partner.title}
          category={partner.category}
          logoUrl={partner.logoUrl}
          coverUrl={partner.coverUrl}
          variant="detail"
        />
      </Group>
      <Group>
        <FormItem top="Название">
          <Input value={partner.title} onChange={(event) => update("title", event.target.value)} />
        </FormItem>
        <FormItem top="Описание">
          <Textarea value={partner.description} onChange={(event) => update("description", event.target.value)} />
        </FormItem>
        <FormItem top="Категория">
          <Input value={partner.category} onChange={(event) => update("category", event.target.value)} />
        </FormItem>
        <FormItem top="Логотип URL">
          <Input value={partner.logoUrl || ""} onChange={(event) => update("logoUrl", event.target.value)} />
        </FormItem>
        <FormItem top="Обложка URL">
          <Input value={partner.coverUrl || ""} onChange={(event) => update("coverUrl", event.target.value)} />
        </FormItem>
        <FormItem top="Адрес">
          <Input value={partner.address || ""} onChange={(event) => update("address", event.target.value)} />
        </FormItem>
        <FormItem top="Телефон">
          <Input value={partner.phone || ""} onChange={(event) => update("phone", event.target.value)} />
        </FormItem>
        <FormItem top="Сайт">
          <Input value={partner.websiteUrl || ""} onChange={(event) => update("websiteUrl", event.target.value)} />
        </FormItem>
        <FormItem top="VK-ссылка">
          <Input value={partner.vkUrl || ""} onChange={(event) => update("vkUrl", event.target.value)} />
        </FormItem>
        <Button loading={saving} onClick={save}>
          Сохранить
        </Button>
      </Group>
      {success ? <Snackbar onClose={() => setSuccess("")}>{success}</Snackbar> : null}
    </Page>
  );
}

export function PartnerDiscountsPage() {
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [rangeRules, setRangeRules] = useState<DiscountRangeRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ rules: DiscountRule[]; rangeRules: DiscountRangeRule[] }>("/partner/discounts")
      .then((response) => {
        const byLevel = new Map(response.data.rules.map((rule) => [rule.membershipLevel, rule]));
        setRules(
          membershipLevels.map(
            (level) =>
              byLevel.get(level) || {
                membershipLevel: level,
                discountPercent: 0,
                minPurchaseAmount: null,
                conditionsText: "Скидка действует при предъявлении активной членской карты.",
                startsAt: null,
                endsAt: null,
                isActive: false
              }
          )
        );
        setRangeRules(response.data.rangeRules || []);
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedPartnerId]);

  const updateRule = (level: string, patch: Partial<DiscountRule>) => {
    setRules((current) => current.map((rule) => (rule.membershipLevel === level ? { ...rule, ...patch } : rule)));
  };

  const updateRangeRule = (index: number, patch: Partial<DiscountRangeRule>) => {
    setRangeRules((current) => current.map((rule, currentIndex) => (currentIndex === index ? { ...rule, ...patch } : rule)));
  };

  const addRangeRule = () => {
    setRangeRules((current) => [
      ...current,
      {
        title: "Фиксированная скидка",
        minPurchaseAmount: 20000,
        maxPurchaseAmount: null,
        discountAmount: 1000,
        conditionsText: "Фиксированная скидка действует при предъявлении активной членской карты.",
        startsAt: null,
        endsAt: null,
        isActive: true
      }
    ]);
  };

  const removeRangeRule = (index: number) => {
    setRangeRules((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await api.patch<{ rules: DiscountRule[]; rangeRules: DiscountRangeRule[] }>("/partner/discounts", {
        rules: rules.map((rule) => ({
          ...rule,
          startsAt: fromDateTime(rule.startsAt),
          endsAt: fromDateTime(rule.endsAt)
        })),
        rangeRules: rangeRules.map((rule) => ({
          ...rule,
          startsAt: fromDateTime(rule.startsAt),
          endsAt: fromDateTime(rule.endsAt)
        }))
      });
      setRules(response.data.rules);
      setRangeRules(response.data.rangeRules || []);
      setSuccess("Скидки сохранены");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page title="Скидки">
        <LoadingState />
      </Page>
    );
  }

  if (error && rules.length === 0) {
    return (
      <Page title="Скидки">
        <AccessDenied />
      </Page>
    );
  }

  return (
    <Page title="Скидки партнёра">
      <PartnerScopeSelector />
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      {rules.map((rule) => (
        <Group key={rule.membershipLevel} header={<Header>{rule.membershipLevel}</Header>}>
          <div className="form-row">
            <FormItem top="Процент">
              <Input
                type="number"
                value={String(rule.discountPercent)}
                onChange={(event) => updateRule(rule.membershipLevel, { discountPercent: Number(event.target.value) })}
              />
            </FormItem>
            <FormItem top="Минимальная покупка">
              <Input
                type="number"
                value={rule.minPurchaseAmount === null ? "" : String(rule.minPurchaseAmount)}
                onChange={(event) =>
                  updateRule(rule.membershipLevel, {
                    minPurchaseAmount: event.target.value ? Number(event.target.value) : null
                  })
                }
              />
            </FormItem>
          </div>
          <FormItem top="Условия">
            <Textarea value={rule.conditionsText} onChange={(event) => updateRule(rule.membershipLevel, { conditionsText: event.target.value })} />
          </FormItem>
          <div className="form-row">
            <FormItem top="Начало">
              <Input type="datetime-local" value={toDateTime(rule.startsAt)} onChange={(event) => updateRule(rule.membershipLevel, { startsAt: event.target.value })} />
            </FormItem>
            <FormItem top="Окончание">
              <Input type="datetime-local" value={toDateTime(rule.endsAt)} onChange={(event) => updateRule(rule.membershipLevel, { endsAt: event.target.value })} />
            </FormItem>
          </div>
          <Checkbox checked={rule.isActive} onChange={(event) => updateRule(rule.membershipLevel, { isActive: event.currentTarget.checked })}>
            Правило активно
          </Checkbox>
        </Group>
      ))}
      <Group
        header={
          <Header
            aside={
              <Button size="s" mode="secondary" onClick={addRangeRule}>
                Добавить
              </Button>
            }
          >
            Фиксированные скидки по сумме покупки
          </Header>
        }
      >
        {rangeRules.length ? (
          rangeRules.map((rule, index) => (
            <Card key={rule.id || index} mode="outline" style={{ margin: "12px 0" }}>
              <FormItem top="Название правила">
                <Input value={rule.title} onChange={(event) => updateRangeRule(index, { title: event.target.value })} />
              </FormItem>
              <div className="form-row">
                <FormItem top="Покупка от">
                  <Input
                    type="number"
                    value={String(rule.minPurchaseAmount)}
                    onChange={(event) => updateRangeRule(index, { minPurchaseAmount: Number(event.target.value) })}
                  />
                </FormItem>
                <FormItem top="Покупка до">
                  <Input
                    type="number"
                    value={rule.maxPurchaseAmount === null ? "" : String(rule.maxPurchaseAmount)}
                    onChange={(event) =>
                      updateRangeRule(index, {
                        maxPurchaseAmount: event.target.value ? Number(event.target.value) : null
                      })
                    }
                    placeholder="Без ограничения"
                  />
                </FormItem>
              </div>
              <FormItem top="Сумма скидки">
                <Input
                  type="number"
                  value={String(rule.discountAmount)}
                  onChange={(event) => updateRangeRule(index, { discountAmount: Number(event.target.value) })}
                />
              </FormItem>
              <FormItem top="Условия">
                <Textarea value={rule.conditionsText} onChange={(event) => updateRangeRule(index, { conditionsText: event.target.value })} />
              </FormItem>
              <div className="form-row">
                <FormItem top="Начало">
                  <Input type="datetime-local" value={toDateTime(rule.startsAt)} onChange={(event) => updateRangeRule(index, { startsAt: event.target.value })} />
                </FormItem>
                <FormItem top="Окончание">
                  <Input type="datetime-local" value={toDateTime(rule.endsAt)} onChange={(event) => updateRangeRule(index, { endsAt: event.target.value })} />
                </FormItem>
              </div>
              <div className="toolbar" style={{ padding: "0 12px 12px" }}>
                <Checkbox checked={rule.isActive} onChange={(event) => updateRangeRule(index, { isActive: event.currentTarget.checked })}>
                  Правило активно
                </Checkbox>
                <Button mode="tertiary" onClick={() => removeRangeRule(index)}>
                  Удалить
                </Button>
              </div>
            </Card>
          ))
        ) : (
          <EmptyState text="Добавьте правило, например: покупка от 20 000 ₽ даёт скидку 1 000 ₽." />
        )}
      </Group>
      <Button loading={saving} onClick={save}>
        Сохранить правила
      </Button>
      {success ? <Snackbar onClose={() => setSuccess("")}>{success}</Snackbar> : null}
    </Page>
  );
}

export function PartnerOffersPage() {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    discountText: "",
    status: "active" as Offer["status"]
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selectedPartnerId = useAuthStore((state) => state.selectedPartnerId);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ offers: Offer[] }>("/partner/offers");
      setOffers(response.data.offers);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [selectedPartnerId]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/partner/offers", form);
      setForm({ title: "", description: "", discountText: "", status: "active" });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (offer: Offer) => {
    await api.patch(`/partner/offers/${offer.id}`, { status: offer.status === "active" ? "inactive" : "active" });
    await load();
  };

  const archive = async (offer: Offer) => {
    await api.delete(`/partner/offers/${offer.id}`);
    await load();
  };

  if (loading) {
    return (
      <Page title="Акции">
        <LoadingState />
      </Page>
    );
  }

  if (error && offers.length === 0) {
    return (
      <Page title="Акции">
        <AccessDenied />
      </Page>
    );
  }

  return (
    <Page title="Акции партнёра">
      <PartnerScopeSelector />
      {error ? <Banner header="Ошибка" subheader={error} /> : null}
      <Group header={<Header>Создать акцию</Header>}>
        <FormItem top="Название">
          <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
        </FormItem>
        <FormItem top="Описание">
          <Textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </FormItem>
        <FormItem top="Текст скидки">
          <Input value={form.discountText} onChange={(event) => setForm({ ...form, discountText: event.target.value })} />
        </FormItem>
        <FormItem top="Статус">
          <Select
            value={form.status}
            onChange={(event) => setForm({ ...form, status: event.target.value as Offer["status"] })}
            options={[
              { label: "Активна", value: "active" },
              { label: "Выключена", value: "inactive" }
            ]}
          />
        </FormItem>
        <Button loading={saving} onClick={create}>
          Создать
        </Button>
      </Group>

      <Group header={<Header>Список акций</Header>}>
        {offers.length ? (
          <CardGrid size="l">
            {offers.map((offer) => (
              <Card key={offer.id} mode="shadow">
                <Cell subtitle={`${offer.description} · ${offer.discountText}`} after={offer.status}>
                  {offer.title}
                </Cell>
                <div className="toolbar" style={{ padding: "0 12px 12px" }}>
                  <Button size="s" mode="secondary" onClick={() => toggle(offer)}>
                    {offer.status === "active" ? "Выключить" : "Включить"}
                  </Button>
                  <Button size="s" mode="tertiary" onClick={() => archive(offer)}>
                    Архивировать
                  </Button>
                </div>
              </Card>
            ))}
          </CardGrid>
        ) : (
          <EmptyState text="Акций пока нет." />
        )}
      </Group>
    </Page>
  );
}
